import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { ResultOf } from 'gql.tada'
import type { LookupResponse } from 'ipdata'

import { graphql_call } from '@repo/common/queries/helpers/server_query'

import { useGetFlags } from '../../hooks/flags'
import { useIsAuthenticated } from '../auth'
import type { InputsOf } from '../helpers/utilities'

import {
  mutation_save_location_attestation,
  query_get_location_attestation,
} from './location_graphql'

export const keys = createQueryKeyStore({
  location: {
    verification: null,
    ip_data: null,
  },
})

export function useGetIpData() {
  const api_key = import.meta.env.VITE_IP_DATA_KEY
  return useQuery<LookupResponse>({
    queryKey: keys.location.ip_data.queryKey,
    queryFn: async () => {
      const response = await fetch(`https://api.ipdata.co?api-key=${api_key}`)

      if (!response.ok) {
        throw new Error('HTTP error ' + response.status)
      }

      return (await response.json()) as LookupResponse
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export type VerificationType = ResultOf<
  typeof query_get_location_attestation
>['me']['location_attestation']['verification']

export function useGetLocationAttestation() {
  const { is_demo_site } = useGetFlags()
  const queryClient = useQueryClient()
  const is_auth = useIsAuthenticated()

  const enabled = is_auth

  return useQuery<VerificationType>({
    queryKey: keys.location.verification.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          if (is_demo_site) {
            return (
              queryClient.getQueryData<VerificationType>(
                keys.location.verification.queryKey,
              ) ??
              ({
                passed: false,
                status: 'missing',
              } satisfies VerificationType)
            )
          }

          const result = await graphql_call({
            query: query_get_location_attestation,
          })
          return result!.location_attestation.verification
        },
    gcTime: Infinity,
    staleTime: Infinity,
  })
}

export function useSaveLocationAttestation() {
  const queryClient = useQueryClient()
  const ip_data = useGetIpData()
  const { country_code, ip: ip_address } = ip_data.data ?? {}

  const isReady = country_code != null && ip_address != null

  const result = useMutation({
    mutationFn: async ({
      agreed,
    }: Pick<InputsOf<typeof mutation_save_location_attestation>, 'agreed'>) => {
      if (!isReady) {
        console.warn('called before enabled')
        return
      }
      return graphql_call({
        query: mutation_save_location_attestation,
        variables: { input: { agreed, country_code, ip_address } },
      })
    },
    onSettled: (call_result) => {
      if (call_result?.passed != null) {
        queryClient.setQueryData<VerificationType>(
          keys.location.verification.queryKey,
          {
            passed: call_result.passed,
            status: 'completed',
          },
        )
      }
    },
  })
  return {
    ...result,
    isReady,
  }
}
